import { useState, type ReactNode, type CSSProperties } from "react";
import { useIsMobile } from "./useIsMobile";

/* ============================ Button ============================ */
type ButtonVariant = "primary" | "secondary" | "ghost" | "sacred" | "danger";
export function Button({
  children,
  variant = "secondary",
  size = "md",
  iconLeft,
  iconRight,
  disabled = false,
  type = "button",
  onClick,
  style,
}: {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  disabled?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
  style?: CSSProperties;
}) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const sizes = {
    sm: { padding: "6px 12px", fontSize: "var(--text-sm)", gap: "6px", radius: "var(--radius-sm)" },
    md: { padding: "9px 16px", fontSize: "var(--text-base)", gap: "8px", radius: "var(--radius-md)" },
    lg: { padding: "12px 22px", fontSize: "var(--text-md)", gap: "10px", radius: "var(--radius-md)" },
  } as const;
  const s = sizes[size];
  const palettes: Record<ButtonVariant, any> = {
    primary: { bg: "var(--accent)", bgHover: "var(--accent-hover)", bgActive: "var(--accent-active)", fg: "var(--text-on-accent)", border: "var(--accent)", borderHover: "var(--accent-hover)" },
    secondary: { bg: "var(--bg-raised)", bgHover: "var(--bg-surface)", bgActive: "var(--bg-sunken)", fg: "var(--text-primary)", border: "var(--border-strong)", borderHover: "var(--accent)" },
    ghost: { bg: "transparent", bgHover: "var(--accent-soft)", bgActive: "var(--accent-ring)", fg: "var(--accent-text)", border: "transparent", borderHover: "transparent" },
    sacred: { bg: "var(--sacred)", bgHover: "var(--gold-700)", bgActive: "var(--gold-700)", fg: "#fff", border: "var(--sacred)", borderHover: "var(--gold-700)" },
    danger: { bg: "var(--bg-raised)", bgHover: "var(--danger-soft)", bgActive: "var(--danger-soft)", fg: "var(--danger)", border: "var(--danger-ring)", borderHover: "var(--danger)" },
  };
  const p = palettes[variant];
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: s.gap,
        fontFamily: "var(--font-ui)", fontWeight: "var(--weight-semibold)" as any, fontSize: s.fontSize,
        lineHeight: 1, padding: s.padding, borderRadius: s.radius,
        border: `1px solid ${hover && !disabled ? p.borderHover : p.border}`,
        background: disabled ? p.bg : active ? p.bgActive : hover ? p.bgHover : p.bg,
        color: p.fg, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1,
        transform: active && !disabled ? "translateY(0.5px)" : "none",
        transition: "background var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out)",
        whiteSpace: "nowrap", ...style,
      }}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  );
}

/* ============================ IconButton ============================ */
export function IconButton({
  children,
  variant = "ghost",
  size = "md",
  disabled = false,
  onClick,
  "aria-label": ariaLabel,
  style,
}: {
  children?: ReactNode;
  variant?: "ghost" | "secondary" | "sacred";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  onClick?: () => void;
  "aria-label"?: string;
  style?: CSSProperties;
}) {
  const [hover, setHover] = useState(false);
  const isMobile = useIsMobile();
  // Larger touch targets on phones (≥40px) so non-technical users tap reliably.
  const d = (isMobile ? { sm: 40, md: 44, lg: 44 } : { sm: 28, md: 34, lg: 40 })[size];
  const palettes: Record<string, any> = {
    ghost: { bg: "transparent", bgHover: "var(--accent-soft)", fg: "var(--text-secondary)", fgHover: "var(--accent-text)", border: "transparent" },
    secondary: { bg: "var(--bg-raised)", bgHover: "var(--bg-surface)", fg: "var(--text-secondary)", fgHover: "var(--text-primary)", border: "var(--border)" },
    sacred: { bg: "var(--sacred-soft)", bgHover: "var(--sacred-ring)", fg: "var(--sacred-text)", fgHover: "var(--sacred-text)", border: "var(--sacred-ring)" },
  };
  const p = palettes[variant];
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", width: d, height: d,
        borderRadius: "var(--radius-md)", border: `1px solid ${p.border}`,
        background: hover && !disabled ? p.bgHover : p.bg, color: hover && !disabled ? p.fgHover : p.fg,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1,
        transition: "background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)", ...style,
      }}
    >
      {children}
    </button>
  );
}

/* ============================ Field ============================ */
export function Field({
  label,
  action,
  hint,
  children,
  style,
}: {
  label: ReactNode;
  action?: ReactNode;
  hint?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", ...style }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "8px", minHeight: 24 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontFamily: "var(--font-ui)", fontWeight: "var(--weight-semibold)" as any, fontSize: "var(--text-sm)", color: "var(--text-primary)" }}>
          {label}
        </span>
        {action}
      </div>
      {children}
      {hint && <div style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>{hint}</div>}
    </div>
  );
}

/* ============================ TextInput ============================ */
export function TextInput({
  value,
  onChange,
  placeholder,
  mono = false,
  style,
}: {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  style?: CSSProperties;
}) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", boxSizing: "border-box", background: "var(--bg-raised)", border: `1px solid ${focus ? "var(--accent)" : "var(--border-strong)"}`, borderRadius: "var(--radius-md)", padding: "0 12px", boxShadow: focus ? "0 0 0 var(--ring-width) var(--focus-ring)" : "none", transition: "border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)", ...style }}>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange && onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontFamily: mono ? "var(--font-ref)" : "var(--font-ui)", fontSize: "var(--text-base)", color: "var(--text-primary)", padding: "10px 0", minWidth: 0 }}
      />
    </div>
  );
}

/* ============================ TextArea ============================ */
export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
  serif = false,
  autoGrow = true,
  style,
}: {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  rows?: number;
  serif?: boolean;
  autoGrow?: boolean;
  style?: CSSProperties;
}) {
  const [focus, setFocus] = useState(false);

  // Auto-grow via the CSS "replicated content" grid technique (ui/tokens.css /
  // styles.css): a ::after pseudo mirrors the text and sizes the cell, so the
  // field always fits its content with no JS measurement / timing issues.
  if (autoGrow) {
    return (
      <div
        className={`ta-grow${serif ? " serif" : ""}`}
        data-replicated-value={(value || "") + " "}
        style={{ ["--rows" as any]: rows, ...style }}
      >
        <textarea
          value={value}
          placeholder={placeholder}
          rows={1}
          onChange={(e) => onChange && onChange(e.target.value)}
        />
      </div>
    );
  }

  return (
    <textarea
      value={value}
      rows={rows}
      placeholder={placeholder}
      onChange={(e) => onChange && onChange(e.target.value)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      style={{
        width: "100%", boxSizing: "border-box",
        fontFamily: serif ? "var(--font-serif)" : "var(--font-ui)",
        fontSize: serif ? "var(--text-md)" : "var(--text-base)",
        lineHeight: "var(--leading-normal)", color: "var(--text-primary)",
        background: "var(--bg-raised)",
        border: `1px solid ${focus ? "var(--accent)" : "var(--border-strong)"}`,
        borderRadius: "var(--radius-md)", padding: "11px 13px",
        resize: "vertical", outline: "none",
        boxShadow: focus ? "0 0 0 var(--ring-width) var(--focus-ring)" : "none",
        transition: "border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)",
        ...style,
      }}
    />
  );
}

/* ============================ Select ============================ */
export function Select({
  value,
  onChange,
  children,
  style,
}: {
  value: string;
  onChange?: (v: string) => void;
  children?: ReactNode;
  style?: CSSProperties;
}) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex", width: "100%", ...style }}>
      <select
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{ appearance: "none", WebkitAppearance: "none", width: "100%", boxSizing: "border-box", fontFamily: "var(--font-ui)", fontSize: "var(--text-base)", color: "var(--text-primary)", background: "var(--bg-raised)", border: `1px solid ${focus ? "var(--accent)" : "var(--border-strong)"}`, borderRadius: "var(--radius-md)", padding: "10px 36px 10px 13px", cursor: "pointer", outline: "none", boxShadow: focus ? "0 0 0 var(--ring-width) var(--focus-ring)" : "none", transition: "border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)" }}
      >
        {children}
      </select>
      <span aria-hidden="true" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)", display: "inline-flex" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
      </span>
    </div>
  );
}

/* ============================ Checkbox ============================ */
export function Checkbox({
  checked,
  onChange,
  label,
  style,
}: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  label?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: "10px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: "var(--text-base)", color: "var(--text-primary)", ...style }}>
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "var(--radius-xs)", border: `1.5px solid ${checked ? "var(--accent)" : "var(--border-strong)"}`, background: checked ? "var(--accent)" : "var(--bg-raised)", color: "#fff", flexShrink: 0, transition: "background var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out)" }}>
        {checked && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange && onChange(e.target.checked)} style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
      {label}
    </label>
  );
}
